import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onDownloadClick: (platform: 'android' | 'ios') => void;
}

export default function HeroSection({ onDownloadClick }: HeroSectionProps) {
  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Icon name="SparklesIcon" size={20} variant="solid" />
              <span className="text-sm font-semibold">Download Now & Get 30 Days Free Premium</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-text-primary mb-6 leading-tight">
              Your Stories,<br />
              <span className="text-primary">Anywhere, Anytime</span>
            </h1>

            <p className="text-lg md:text-xl text-text-secondary mb-8 max-w-2xl">
              Download StoryNest-FM and dive into India's largest collection of audio stories. From thrilling mysteries to heartwarming romances, discover stories that speak to your soul in your language.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <button
                onClick={() => onDownloadClick('android')}
                className="group flex items-center justify-center space-x-3 bg-text-primary text-white px-8 py-4 rounded-xl font-cta font-bold transition-all duration-300 hover:bg-text-primary/90 hover:shadow-warm-lg hover:scale-105">

                <Icon name="DevicePhoneMobileIcon" size={24} />
                <div className="text-left">
                  <div className="text-xs opacity-80">Download on</div>
                  <div className="text-lg">Google Play</div>
                </div>
              </button>

              <button
                onClick={() => onDownloadClick('ios')}
                className="group flex items-center justify-center space-x-3 bg-text-primary text-white px-8 py-4 rounded-xl font-cta font-bold transition-all duration-300 hover:bg-text-primary/90 hover:shadow-warm-lg hover:scale-105">

                <Icon name="DevicePhoneMobileIcon" size={24} />
                <div className="text-left">
                  <div className="text-xs opacity-80">Download on</div>
                  <div className="text-lg">App Store</div>
                </div>
              </button>
            </div>

            <div className="flex items-center justify-center lg:justify-start space-x-8 text-sm text-text-secondary">
              <div className="flex items-center space-x-2">
                <Icon name="StarIcon" size={20} variant="solid" className="text-accent" />
                <span className="font-semibold">4.8 Rating</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ArrowDownTrayIcon" size={20} className="text-primary" />
                <span className="font-semibold">5M+ Downloads</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="UserGroupIcon" size={20} className="text-secondary" />
                <span className="font-semibold">2M+ Active Users</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative z-10 max-w-md mx-auto">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_171cbef36-1765875815808.png"
                alt="Modern smartphone displaying StoryNest-FM app interface with colorful story cards and audio player controls on screen"
                className="w-full h-auto rounded-3xl shadow-warm-2xl" />

              <div className="absolute -top-6 -right-6 bg-success text-success-foreground px-6 py-3 rounded-2xl shadow-warm-lg transform rotate-6">
                <div className="text-sm font-semibold">Free Premium</div>
                <div className="text-2xl font-bold">30 Days</div>
              </div>
            </div>

            <div className="absolute top-1/2 -left-8 bg-card p-4 rounded-2xl shadow-warm-lg transform -translate-y-1/2 hidden lg:block">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Icon name="PlayIcon" size={24} variant="solid" className="text-primary" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-text-primary">50,000+</div>
                  <div className="text-xs text-text-secondary">Audio Stories</div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-8 -right-8 bg-card p-4 rounded-2xl shadow-warm-lg hidden lg:block">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                  <Icon name="LanguageIcon" size={24} className="text-secondary" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-text-primary">15+ Languages</div>
                  <div className="text-xs text-text-secondary">Regional Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}